import { isPresent } from '@fcom/core/utils';
import { Category, FinnairBoundItem, FinnairServiceCatalogV3 } from '@fcom/dapi/api/models';

import { isFlight } from '../../../utils/flight.utils';

export interface FragmentQuota {
  fragmentId: string;
  quota: number;
}

interface QuotaBearingService {
  isService?: boolean;
  quota?: number;
  variant: string;
}

type QuotaBearingServicesPerTraveler = Record<string, QuotaBearingService[]>;
type QuotaBearingServiceCatalogServices = Record<string, QuotaBearingServicesPerTraveler>;

export const SUFFICIENT_QUOTA_SENTINEL = 9;

export const LOW_QUOTA_THRESHOLD = 8;

export const isLowQuota = (remaining: number | undefined): boolean =>
  isPresent(remaining) && remaining > 0 && remaining <= LOW_QUOTA_THRESHOLD;

export const CATEGORY_LEVEL_QUOTA_CATEGORIES: Category[] = [Category.MEAL, Category.PRIORITY];
export const VARIANT_LEVEL_QUOTA_CATEGORIES: Category[] = [Category.SPORT];

export const hasCategoryLevelQuota = (category: Category): boolean =>
  CATEGORY_LEVEL_QUOTA_CATEGORIES.includes(category);

export const lowestQuotaAcrossServiceVariants = (
  services: QuotaBearingServicesPerTraveler | undefined
): number | undefined => {
  const quotas = Object.values(services ?? {})
    .flat()
    .filter(Boolean)
    .filter(({ isService }) => isService)
    .map(({ quota }) => quota)
    .filter(isPresent);

  return quotas.length ? Math.min(...quotas) : undefined;
};

export const lowestQuotaAcrossFlights = (
  services: QuotaBearingServiceCatalogServices | undefined,
  initeraryFlightIdsInTravelOrder: string[]
): FragmentQuota | undefined =>
  initeraryFlightIdsInTravelOrder.reduce<FragmentQuota | undefined>((lowestFragment, flightId) => {
    const quota = lowestQuotaAcrossServiceVariants(services?.[flightId]);

    if (!isPresent(quota)) {
      return lowestFragment;
    }

    const currentFragmentQuota: FragmentQuota = { fragmentId: flightId, quota };

    if (!lowestFragment) {
      return currentFragmentQuota;
    }

    return currentFragmentQuota.quota < lowestFragment.quota ? currentFragmentQuota : lowestFragment;
  }, undefined);

export const getCategoryLevelQuotaCount = (
  serviceCatalog: FinnairServiceCatalogV3 | undefined,
  category: Category,
  bound: FinnairBoundItem
): number | undefined => {
  if (!hasCategoryLevelQuota(category) || !serviceCatalog) {
    return undefined;
  }

  const catalogForCategory = serviceCatalog.categories.find((item) => item.category === category);
  if (!catalogForCategory) {
    return 0;
  }

  const itineraryFlightId = bound.itinerary.filter(isFlight).map((flight) => flight.id);
  const serviceAvailableForBound = itineraryFlightId.some((id) => Boolean(catalogForCategory.services?.[id]));
  if (!serviceAvailableForBound) {
    return 0;
  }

  const lowestQuota = lowestQuotaAcrossFlights(catalogForCategory.services, itineraryFlightId)?.quota;

  return lowestQuota ?? SUFFICIENT_QUOTA_SENTINEL;
};
